import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import PropertyComponent from './PropertyComponent';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { IconButton, TableBody, TableRow, TableHead } from '@mui/material';
import { Add } from '@mui/icons-material';
import { StyledTable, StyledHeaderCell } from 'components/common/Styles';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import usePropertiesData from 'features/common/components/property-selector/usePropertiesData';

const PropertiesContainer = ({ partnerTypeId, lens, initialData, validation, disabled, onChangeProperty, onDeleteProperty }) => {
  const [t] = useTranslation();
  const client = useApolloClient();
  const confirm = useConfirmDialog();

  const { properties } = usePropertiesData();

  // #region Handlers

  const handleAdd = useCallback(() => {
    const items = lens |> get;
    const newProperty = {
      key: crypto.randomUUID(),
      partnerTypeId,
      propertyId: null,
      propertyName: null,
      defaultValue: null,
      mandatory: null,
      editing: true
    };
    (lens |> set)([newProperty, ...items]);
  }, [lens, partnerTypeId]);

  const handleEdit = useCallback(
    propertyId => {
      const items = lens |> get;
      const updated = items.map(x => (x.partnerTypeId === partnerTypeId && x.propertyId === propertyId ? { ...x, editing: true } : x));
      (lens |> set)(updated);
    },
    [lens, partnerTypeId]
  );

  const handleUndoChanges = useCallback(() => {
    (lens |> set)(initialData.properties);
  }, [lens, initialData]);

  const handleDelete = useCallback(
    propertyId => {
      confirm(
        () => {
          onDeleteProperty(partnerTypeId, propertyId);
          client.cache.evict({
            id: `PartnerTypeProperty:{"partnerTypeId":${partnerTypeId}, "propertyId":${propertyId}}`
          });
          client.cache.gc();
        },
        {
          title: t('PartnerType.Details.PartnerTypeXProperty.DeleteProperty'),
          content: t('PartnerType.Details.PartnerTypeXProperty.AreYouSureDeleteProperty')
        }
      );
    },
    [confirm, t, partnerTypeId, client.cache, onDeleteProperty]
  );

  // #endregion

  return (
    <Fragment>
      <StyledTable sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <StyledHeaderCell>{t('PartnerType.Details.PartnerTypeXProperty.Name')}</StyledHeaderCell>
            <StyledHeaderCell>{t('PartnerType.Details.PartnerTypeXProperty.DefaultValue')}</StyledHeaderCell>
            <StyledHeaderCell>{t('PartnerType.Details.PartnerTypeXProperty.Mandatory')}</StyledHeaderCell>
            <StyledHeaderCell align='right'>
              <IconButton title={t('Tooltips.Add')} onClick={handleAdd} disabled={disabled} color='primary'>
                <Add />
              </IconButton>
            </StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(lens.length |> get) > 0 &&
            (lens |> sequence).map((propertyLens, index) => (
              <PropertyComponent
                key={get(propertyLens.key)}
                lens={propertyLens}
                properties={properties}
                validation={validation?.properties[index]}
                disabled={disabled}
                onChangeProperty={onChangeProperty}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCancelAdd={handleUndoChanges}
              />
            ))}
        </TableBody>
      </StyledTable>
    </Fragment>
  );
};

PropertiesContainer.propTypes = {
  partnerTypeId: PropTypes.number,
  lens: PropTypes.object,
  initialData: PropTypes.object,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  canWrite: PropTypes.bool,
  onChangeProperty: PropTypes.func,
  onDeleteProperty: PropTypes.func
};
export default PropertiesContainer;
